import { Component, input, ViewChild, ElementRef, HostListener, Inject, OnInit, inject } from '@angular/core';
import { DOCUMENT, NgClass, NgFor, NgIf } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { GetUserInstructionDetails } from '@cureskin/api-client/src/server-api';
import { ConnectionService } from '@services/connection-service';
import { AppConfig } from 'client/app/app.config';
import { Table } from '@cureskin/api-client';
import { InstructionCardComponent } from '@shared/regimen-instruction/instruction-card/instruction-card.component';
import { InstructionsService, FormattedInstructionData, DayName } from '@services/instructions-service/instructions.service';
import { EventLoggerService } from '@services/event-logger-service';
import { REGIMEN_ROUTE_PATHS } from '../user-regimen/regimen.constants';

export interface CalendarDay {
  dayValue: string;
  month: string;
  date: number;
  fullDate: Date;
  isSelected: boolean;
}

export type MonthName = 'Jan' | 'Feb' | 'Mar' | 'Apr' | 'May' | 'Jun' | 'July' | 'Aug' | 'Sept' | 'Oct' | 'Nov' | 'Dec';

@Component({
  selector: 'user-view-instructions',
  imports: [InstructionCardComponent, NgFor, NgIf, NgClass],
  templateUrl: './user-view-instructions.component.html',
})
export class UserViewInstructionsComponent implements OnInit {
  @ViewChild('videoPlayer', { static: false }) videoPlayer: ElementRef<HTMLVideoElement>;

  private readonly activatedRoute = inject(ActivatedRoute);
  addOnInstructions = input<GetUserInstructionDetails['userInstructions']['ADDITIONAL_PRODUCTS']>();
  isRouteMode = false;
  user: Table.User;
  userLanguage: string;
  productInstructions: FormattedInstructionData[] = [];
  weekArray: CalendarDay[] = [];
  selectedDate: Date = new Date();
  todayDay: number;
  todayDayVal: number;
  unChangedTodayDay: number = new Date().getDate();
  isDayTime: boolean = true;
  days: DayName[] = ['Sun', 'Mon', 'Tue', 'Wed', 'Thr', 'Fri', 'Sat'];
  monthNames: MonthName[] = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'July', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];
  isPlaying: boolean = false;

  constructor(
    private readonly conntionService: ConnectionService,
    private readonly appConfig: AppConfig,
    private readonly instructionService: InstructionsService,
    private readonly eventLoggerService: EventLoggerService,
    @Inject(DOCUMENT) private  readonly document: Document
  ) {}

  @HostListener('document:fullscreenchange')
  onFullscreenChange(): void {
    if (!this.document.fullscreenElement) {
      this.closeVideo();
    }
  }

  ngOnInit(): void {
    this.isRouteMode = this.activatedRoute.snapshot.routeConfig?.path === REGIMEN_ROUTE_PATHS.INSTRUCTIONS;
    this.getWeeklyCalendar();
    this.initializeDayTime();
    this.user = this.conntionService.getActingUser();
    this.userLanguage = this.user?.get('languagePreference') || this.appConfig.Shared.Languages.EN;

    const instructions = this.isRouteMode
      ? this.instructionService.getAddOnInstructions()
      : (this.addOnInstructions() ?? []);
    this.productInstructions = instructions.map((each) => this.instructionService.generateCommonDataForInstruction(each.PRODUCT));

    this.eventLoggerService.cleverTapEvent('impression', JSON.stringify({
      name: 'instructions_others'
    }));
  }

  private initializeDayTime(): void {
    const MORNING_START_HOUR = 6;
    const NIGHT_START_HOUR = 20;

    this.todayDay = new Date().getDate();
    this.todayDayVal = new Date().getDay();

    const hours = new Date().getHours();
    this.isDayTime = hours >= MORNING_START_HOUR && hours < NIGHT_START_HOUR;
  }

  getWeeklyCalendar(): void {
    this.weekArray = [];
    const currentDate = new Date();

    // Place today in the middle of the week array (-3 to +3 days)
    for (let i = -3; i <= 3; i += 1) {
      const day = new Date(currentDate);
      day.setDate(currentDate.getDate() + i);
      const dayValue = this.days[day.getDay()];
      const month = this.monthNames[day.getMonth()];
      const date = day.getDate();

      const obj = {
        dayValue,
        month,
        date,
        fullDate: day,
        isSelected: this.isSameDay(day, this.selectedDate),
      };
      this.weekArray.push(obj);
    }
  }

  private isSameDay(date1: Date, date2: Date): boolean {
    return date1.getDate() === date2.getDate()
           && date1.getMonth() === date2.getMonth()
           && date1.getFullYear() === date2.getFullYear();
  }

  onDateSelect(selectedDay: CalendarDay): void {
    this.selectedDate = new Date(selectedDay.fullDate);

    this.weekArray = this.weekArray.map((day: CalendarDay) => ({
      ...day,
      isSelected: this.isSameDay(day.fullDate, this.selectedDate),
    }));

    this.todayDay = selectedDay.fullDate.getDate();
    this.todayDayVal = selectedDay.fullDate.getDay();

    const name = 'others_instructions_date_changed';
    this.eventLoggerService.cleverTapEvent('click', JSON.stringify({ name }));
  }

  onPlayButtonClick(instructionItem: FormattedInstructionData): void {
    this.userLanguage = this.user?.get('languagePreference') || this.appConfig.Shared.Languages.EN;
    if (this.videoPlayer?.nativeElement) {
      this.eventLoggerService.cleverTapEvent('click', JSON.stringify({ name: 'play-product-instruction-video' }));
      const videoPlayer = this.videoPlayer.nativeElement;
      videoPlayer.parentElement.classList.remove('tw-hidden');
      videoPlayer.src = this.instructionService.getInstructionVideoUrl(instructionItem.instructionSet, this.user);
      const playPromise = videoPlayer.play();
      if (playPromise && !this.isPlaying) {
        playPromise.then(():void => {
          this.isPlaying = true;
        });
      }
    }
  }

  closeVideo(): void {
    if (!this.isPlaying) return;
    this.videoPlayer.nativeElement.pause();
    this.videoPlayer.nativeElement.currentTime = 0;
    this.videoPlayer.nativeElement.parentElement.classList.add('tw-hidden');
    this.isPlaying = false;
  }
}
